// src/pages/finance-portal/public/FinancePublicPayment.js
// Public payment page for Finance Portal payment links (no auth required)

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, TextField, Alert, CircularProgress,
    Divider, Chip, InputAdornment, Tabs, Tab,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

const api = axios.create({ baseURL: API_BASE });

function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─────────────────────────────────────────────
// Inner form — uses Stripe hooks
// ─────────────────────────────────────────────

function PaymentForm({ linkData, token, onSuccess }) {
    const stripe = useStripe();
    const elements = useElements();

    const [tab, setTab] = useState(0);
    const [amount, setAmount] = useState('');
    const [payerName, setPayerName] = useState('');
    const [payerEmail, setPayerEmail] = useState('');
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState(null);

    const invoice = linkData.invoice;
    const available = linkData.available_payment_methods || {};
    const methods = Object.entries(available).filter(([, v]) => v).map(([k]) => k);

    useEffect(() => {
        const defaultAmount = invoice?.balance_due || linkData.amount || '';
        setAmount(defaultAmount ? String(defaultAmount) : '');
    }, [invoice, linkData.amount]);

    const methodLabels = { stripe: 'Card', paypal: 'PayPal', coinbase: 'Crypto' };

    // ── STRIPE PAY ──
    const handleStripePay = async () => {
        if (!stripe || !elements) return;
        setProcessing(true);
        setError(null);
        try {
            const processRes = await api.post(`/finance/pay/${token}/process`, {
                amount: parseFloat(amount),
                payment_method: 'stripe',
                payer_name: payerName,
                payer_email: payerEmail,
            });
            if (!processRes.data.success) {
                setError(processRes.data.message);
                setProcessing(false);
                return;
            }

            const { clientSecret, payment_intent_id } = processRes.data;

            const result = await stripe.confirmCardPayment(clientSecret, {
                payment_method: {
                    card: elements.getElement(CardElement),
                    billing_details: {
                        name: payerName || undefined,
                        email: payerEmail || undefined,
                    },
                },
            });

            if (result.error) {
                setError(result.error.message);
                setProcessing(false);
                return;
            }

            if (result.paymentIntent.status === 'succeeded') {
                const verifyRes = await api.post(`/finance/pay/${token}/verify`, {
                    payment_method: 'stripe',
                    payment_intent_id,
                    amount: parseFloat(amount),
                    payer_name: payerName,
                    payer_email: payerEmail,
                });
                if (verifyRes.data.success) {
                    onSuccess(verifyRes.data.data);
                } else {
                    setError(verifyRes.data.message);
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Payment failed');
        } finally {
            setProcessing(false);
        }
    };

    // ── PAYPAL ──
    const createPayPalOrder = async () => {
        const res = await api.post(`/finance/pay/${token}/process`, {
            amount: parseFloat(amount),
            payment_method: 'paypal',
            payer_name: payerName,
            payer_email: payerEmail,
        });
        if (!res.data.success) throw new Error(res.data.message);
        return res.data.paypalOrderId;
    };

    const onPayPalApprove = async (data) => {
        setProcessing(true);
        setError(null);
        try {
            const res = await api.post(`/finance/pay/${token}/verify`, {
                payment_method: 'paypal',
                paypal_order_id: data.orderID,
                amount: parseFloat(amount),
                payer_name: payerName,
                payer_email: payerEmail,
            });
            if (res.data.success) {
                onSuccess(res.data.data);
            } else {
                setError(res.data.message);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'PayPal payment verification failed');
        } finally {
            setProcessing(false);
        }
    };

    // ── COINBASE ──
    const handleCoinbasePay = async () => {
        setProcessing(true);
        setError(null);
        try {
            const res = await api.post(`/finance/pay/${token}/process`, {
                amount: parseFloat(amount),
                payment_method: 'coinbase',
                payer_name: payerName,
                payer_email: payerEmail,
            });
            if (res.data.success && res.data.hosted_url) {
                window.location.href = res.data.hosted_url;
            } else {
                setError(res.data.message || 'Failed to create crypto payment');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Crypto payment failed');
        } finally {
            setProcessing(false);
        }
    };

    const currentMethod = methods[tab] || 'stripe';
    const canPay = parseFloat(amount) > 0 && !processing;

    return (
        <Box>
            {/* Invoice Summary */}
            {invoice && (
                <Paper elevation={0} sx={{ p: 2.5, mb: 3, bgcolor: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#166534', mb: 1 }}>
                        Invoice {invoice.invoice_number}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#374151' }}>
                            Customer: <strong>{invoice.customer?.display_name || invoice.customer?.company_name || ''}</strong>
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#374151' }}>
                            Due: <strong>{formatDate(invoice.due_date)}</strong>
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#374151' }}>
                            Total: <strong>{formatCurrency(invoice.total_amount)}</strong>
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: ACCENT, fontWeight: 700 }}>
                            Balance Due: <strong>{formatCurrency(invoice.balance_due)}</strong>
                        </Typography>
                    </Box>
                </Paper>
            )}

            {/* Payer Info */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <TextField
                    size="small" label="Your Name" value={payerName}
                    onChange={e => setPayerName(e.target.value)}
                    InputLabelProps={{ sx: { fontFamily } }}
                    InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                />
                <TextField
                    size="small" label="Your Email" type="email" value={payerEmail}
                    onChange={e => setPayerEmail(e.target.value)}
                    InputLabelProps={{ sx: { fontFamily } }}
                    InputProps={{ sx: { fontFamily, borderRadius: '10px' } }}
                />
            </Box>

            {/* Amount */}
            <TextField
                size="small" label="Payment Amount" type="number" fullWidth value={amount}
                onChange={e => setAmount(e.target.value)}
                disabled={!linkData.allow_partial && (invoice?.balance_due > 0)}
                InputLabelProps={{ sx: { fontFamily } }}
                InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    sx: { fontFamily, borderRadius: '10px' },
                }}
                sx={{ mb: 3 }}
            />

            {/* Payment Method Tabs */}
            {methods.length > 1 && (
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}
                    TabIndicatorProps={{ sx: { bgcolor: ACCENT } }}>
                    {methods.map((m, i) => (
                        <Tab key={m} label={methodLabels[m] || m} sx={{ fontFamily, fontWeight: 600, textTransform: 'none' }} />
                    ))}
                </Tabs>
            )}

            {/* Stripe Card */}
            {currentMethod === 'stripe' && (
                <Box>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: '10px' }}>
                        <CardElement options={{
                            style: {
                                base: { fontSize: '16px', fontFamily: "'Plus Jakarta Sans', sans-serif", color: '#1e293b' },
                                invalid: { color: '#ef4444' },
                            },
                        }} />
                    </Paper>
                    <Button
                        variant="contained" fullWidth onClick={handleStripePay} disabled={!canPay || !stripe}
                        startIcon={processing ? <CircularProgress size={18} color="inherit" /> : <CreditCardIcon />}
                        sx={{
                            fontFamily, fontWeight: 700, textTransform: 'none', borderRadius: '10px', py: 1.5,
                            bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' }, fontSize: '1rem',
                        }}
                    >
                        {processing ? 'Processing...' : `Pay ${formatCurrency(amount)}`}
                    </Button>
                </Box>
            )}

            {/* PayPal */}
            {currentMethod === 'paypal' && PAYPAL_CLIENT_ID && (
                <Box>
                    {canPay ? (
                        <PayPalButtons
                            style={{ layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay' }}
                            createOrder={createPayPalOrder}
                            onApprove={onPayPalApprove}
                            onError={(err) => setError(err.message || 'PayPal error')}
                        />
                    ) : (
                        <Alert severity="info" sx={{ fontFamily, borderRadius: '10px' }}>
                            Enter a valid amount to proceed with PayPal.
                        </Alert>
                    )}
                </Box>
            )}

            {/* Coinbase */}
            {currentMethod === 'coinbase' && (
                <Button
                    variant="contained" fullWidth onClick={handleCoinbasePay} disabled={!canPay}
                    startIcon={processing ? <CircularProgress size={18} color="inherit" /> : null}
                    sx={{
                        fontFamily, fontWeight: 700, textTransform: 'none', borderRadius: '10px', py: 1.5,
                        bgcolor: '#0052FF', '&:hover': { bgcolor: '#003ECB' }, fontSize: '1rem',
                    }}
                >
                    {processing ? 'Redirecting...' : `Pay ${formatCurrency(amount)} with Crypto`}
                </Button>
            )}

            {error && (
                <Alert severity="error" sx={{ mt: 2, fontFamily, borderRadius: '10px' }}>
                    {error}
                </Alert>
            )}
        </Box>
    );
}


// ─────────────────────────────────────────────
// Outer wrapper — fetches data, wraps providers
// ─────────────────────────────────────────────

export default function FinancePublicPayment() {
    const { token } = useParams();
    const [searchParams] = useSearchParams();

    const [linkData, setLinkData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const fetchLink = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/finance/pay/${token}`);
            if (res.data.success) {
                setLinkData(res.data.data);
            } else {
                setError(res.data.message || 'Payment link not found');
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Failed to load payment link';
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { fetchLink(); }, [fetchLink]);

    // Handle Coinbase redirect back
    useEffect(() => {
        const gateway = searchParams.get('gateway');
        const status = searchParams.get('status');
        const chargeId = searchParams.get('charge_id');
        if (gateway === 'coinbase' && status === 'success' && chargeId && linkData) {
            api.post(`/finance/pay/${token}/verify`, {
                payment_method: 'coinbase',
                charge_id: chargeId,
                amount: linkData.amount || 0,
            }).then(res => {
                if (res.data.success) setSuccess(res.data.data);
            }).catch(() => {});
        }
    }, [searchParams, token, linkData]);

    const handleSuccess = (data) => {
        setSuccess(data);
    };

    // ── LOADING ──
    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    // ── ERROR ──
    if (error) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Paper sx={{ p: 4, maxWidth: 480, width: '100%', textAlign: 'center', borderRadius: '16px' }}>
                    <ErrorOutlineIcon sx={{ fontSize: 56, color: '#ef4444', mb: 2 }} />
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.25rem', mb: 1 }}>
                        Payment Unavailable
                    </Typography>
                    <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem' }}>
                        {error}
                    </Typography>
                </Paper>
            </Box>
        );
    }

    // ── SUCCESS ──
    if (success) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                <Paper sx={{ p: 4, maxWidth: 480, width: '100%', textAlign: 'center', borderRadius: '16px' }}>
                    <CheckCircleIcon sx={{ fontSize: 64, color: ACCENT, mb: 2 }} />
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.5rem', mb: 1, color: '#166534' }}>
                        Payment Successful!
                    </Typography>
                    <Typography sx={{ fontFamily, color: '#475569', mb: 2, fontSize: '0.95rem' }}>
                        Thank you for your payment of {formatCurrency(success.amount)}.
                    </Typography>
                    {success.payment_number && (
                        <Chip label={`Reference: ${success.payment_number}`} sx={{ fontFamily, fontWeight: 600, mb: 2 }} />
                    )}
                    {success.invoice_status && (
                        <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.85rem' }}>
                            Invoice Status: <strong>{success.invoice_status}</strong>
                            {success.invoice_balance_due > 0 && ` | Remaining: ${formatCurrency(success.invoice_balance_due)}`}
                        </Typography>
                    )}
                </Paper>
            </Box>
        );
    }

    // ── PAYMENT FORM ──
    const hasStripe = linkData.available_payment_methods?.stripe;
    const hasPaypal = linkData.available_payment_methods?.paypal;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Paper sx={{ p: { xs: 3, sm: 4 }, maxWidth: 520, width: '100%', borderRadius: '16px' }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.5rem', color: '#0f172a' }}>
                        Make a Payment
                    </Typography>
                    {linkData.description && (
                        <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.9rem', mt: 0.5 }}>
                            {linkData.description}
                        </Typography>
                    )}
                </Box>

                <Divider sx={{ mb: 3 }} />

                {hasStripe && stripePromise ? (
                    <Elements stripe={stripePromise}>
                        {hasPaypal && PAYPAL_CLIENT_ID ? (
                            <PayPalScriptProvider options={{ 'client-id': PAYPAL_CLIENT_ID, currency: 'USD' }}>
                                <PaymentForm linkData={linkData} token={token} onSuccess={handleSuccess} />
                            </PayPalScriptProvider>
                        ) : (
                            <PaymentForm linkData={linkData} token={token} onSuccess={handleSuccess} />
                        )}
                    </Elements>
                ) : hasPaypal && PAYPAL_CLIENT_ID ? (
                    <PayPalScriptProvider options={{ 'client-id': PAYPAL_CLIENT_ID, currency: 'USD' }}>
                        <PaymentForm linkData={linkData} token={token} onSuccess={handleSuccess} />
                    </PayPalScriptProvider>
                ) : (
                    <PaymentForm linkData={linkData} token={token} onSuccess={handleSuccess} />
                )}

                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                        Payments are processed securely. Your information is encrypted.
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
}
